import { Link } from 'react-router-dom';

// Components
import Heading from '@/components/atoms/Heading/Heading';
import CmsImage from '@/components/atoms/CmsImage/CmsImage';

// Utils
import cn from '@/utils/cn';
import { useNormalizedLink } from '@/hooks/useNormalizedLink';

// Other
import { type BlocMarquesType } from '@/services/blocksSchema';
import Vector from '@/assets/blocks/vector-shadow-grey-small.svg?react';

// Style
import styles from './BlocMarques.module.css';

interface BlocMarquesProps {
  title: BlocMarquesType['title'];
  carouselSpeed: BlocMarquesType['carouselSpeed'];
  brands?: BlocMarquesType['brands'];
}

type BrandItem = NonNullable<BlocMarquesType['brands']>[number];

function BrandSlideItem({
  brand,
  normalizeLink,
}: {
  brand: BrandItem;
  normalizeLink: (url: string) => string;
}) {
  return (
    <Link to={normalizeLink(brand.url)} aria-label={brand.brand}>
      {brand.image ? (
        <CmsImage
          media={brand.image}
          sizes="180px"
          className={styles['slide-image']}
          alt={brand.brand}
        />
      ) : (
        <span className={styles['slide-fallback']}>{brand.brand}</span>
      )}
    </Link>
  );
}

function BlocMarques({
  title,
  carouselSpeed,
  brands = null,
}: BlocMarquesProps) {
  const normalizeLink = useNormalizedLink();

  const getSpeed = (speed: string) => {
    switch (speed) {
    case 'lente':
      return 'slow';
    case 'moyenne':
      return 'medium';
    case 'rapide':
      return 'fast';
    default:
      return 'medium';
    };
  };

  const slideClassName = cn([
    styles.slide,
    styles[`slide-${getSpeed(carouselSpeed)}`],
  ]);

  const brandKey = (brand: BrandItem) => `${brand.url}-${brand.brand}`;

  return (
    <div>
      <div className={styles.container}>
        <Heading
          title={title}
          size='h4'
          Tag='h4'
          color='dark'
        />
      </div>
      <div className={styles.logos}>
        <div className={slideClassName}>
          {brands?.map((brand) => (
            <BrandSlideItem
              key={`slide1-${brandKey(brand)}`}
              brand={brand}
              normalizeLink={normalizeLink}
            />
          ))}
        </div>
        <div className={slideClassName}>
          {brands?.map((brand) => (
            <BrandSlideItem
              key={`slide2-${brandKey(brand)}`}
              brand={brand}
              normalizeLink={normalizeLink}
            />
          ))}
        </div>
      </div>
      <Vector />
    </div>
  );
}

export default BlocMarques;
